import { defineComponent as _defineComponent } from 'vue'
import Modal from "@/components/ui/Modal.vue";
  import InputSearch from "@/components/ui/input/InputSearch.vue";
  import { computed, onMounted, reactive, ref, watch } from "vue";
  import store from "@/store";
  import { LeadInterface } from "@/types/LeadInterface";
  import plural from "@/features/plural";
  import { debounce } from "@/helpers/debounce";
  import useVuelidate from "@vuelidate/core";
  import { useToast } from "vue-toastification";
  import { email, required } from "@vuelidate/validators";
  import { ContactInterface } from "@/types/ContactInterface";
  import { SendContractFormType } from "@/enums/SendContractFormType";
  interface LeadInterfaceWithType extends LeadInterface {
    type: SendContractFormType;
  }

  interface ContactInterfaceWithType extends ContactInterface {
    type: SendContractFormType;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'SendContractFormModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    estateId: {
      type: String,
      required: true,
    },
    lotId: {
      type: String,
      required: true,
    },
  },
  emits: ["close"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();
  const emits = __emit;
  const isLoading = computed(() => activeLoaders.value > 0);
  const searchQuery = ref("");
  const leads = ref<LeadInterfaceWithType[]>([]);
  const contacts = ref<ContactInterfaceWithType[]>([]);
  const activeLoaders = ref<number>(0);
  const selectedLeadOrContactId = ref("");

  const leadsAndContactsList = computed(() => {
    const data = [...leads.value, ...contacts.value];

    return data.filter(
      (leadOrContact, index) =>
        data.findIndex((item) => item.email === leadOrContact.email) === index
    );
  });

  const form = reactive({
    firstName: "",
    lastName: "",
    email: "",
    type: null,
  });

  const rules = computed(() => {
    return {
      firstName: { required, $autoDirty: true },
      email: { required, email },
    };
  });
  const v$ = useVuelidate(rules, form);

  const isEmailValid = computed(() => {
    return v$.value.email.$touch();
  });

  const props = __props;

  const getData = () => {
    getLeads();
    getContacts();
  };
  const onSendToLeadOrContact = () => {
    if (!selectedLeadOrContactId.value) return;

    const item = leadsAndContactsList.value.find(
      (item) => item._id === selectedLeadOrContactId.value
    );

    if (!item) return;

    if (item.type === SendContractFormType.CONTACT) {
      shareContactLink();
    } else {
      shareLeadsLinks();
    }
  };
  const getLeads = async () => {
    if (props.estateId) {
      activeLoaders.value++;

      try {
        const response = await store.dispatch("contact/getLeads", {
          estateId: props.estateId,
          query: searchQuery.value,
        });

        leads.value = response.data.map((lead: LeadInterface) => ({
          ...lead,
          type: SendContractFormType.LEAD,
        }));
      } catch (error) {
        toast.error("Something went wrong");
      } finally {
        activeLoaders.value--;
      }
    }
  };

  const getContacts = async () => {
    if (!props.estateId) return;

    activeLoaders.value++;

    try {
      const response = await store.dispatch("contact/getContacts", {
        id: props.estateId,
        buyerStatus: "",
        searchKey: searchQuery.value,
      });

      contacts.value = response.data.map((item: ContactInterface) => ({
        ...item,
        type: SendContractFormType.CONTACT,
      }));
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      activeLoaders.value--;
    }
  };
  const shareLeadsLinks = () => {
    activeLoaders.value++;
    store
      .dispatch("contact/shareLeadsLinks", {
        leadIds: [selectedLeadOrContactId.value],
        lotIds: [props.lotId],
      })
      .then(() => {
        close();
        toast.success("Email was successfully sent");
      })
      .finally(() => {
        activeLoaders.value--;
      });
  };
  const shareContactLink = () => {
    activeLoaders.value++;
    store
      .dispatch("contact/shareEoiLinkWithContacts", {
        contactId: selectedLeadOrContactId.value,
        lotId: props.lotId,
      })
      .then(() => {
        close();
        toast.success("Email was successfully sent");
      })
      .finally(() => {
        activeLoaders.value--;
      });
  };

  const sendReservationLinkToNewLead = () => {
    v$.value.$touch();

    if (v$.value.$invalid) {
      return;
    }

    activeLoaders.value++;
    store
      .dispatch("contact/sendReservationLinkToNewLead", {
        lotIds: [props.lotId],
        personData: {
          estateId: props.estateId,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
        },
      })
      .then(() => {
        close();
        toast.success("Email was successfully sent");
      })
      .finally(() => {
        activeLoaders.value--;
      });
  };
  const itemsCountText = computed(() => {
    const { LEAD, CONTACT } = SendContractFormType;

    const leadsCount = leadsAndContactsList.value.filter(
      ({ type }) => type === LEAD
    ).length;
    const contactsCount = leadsAndContactsList.value.filter(
      ({ type }) => type === CONTACT
    ).length;

    if (!leadsCount && !contactsCount && searchQuery.value) {
      return "No results found. Add a new lead or contact and send the form";
    }

    return `${plural(leadsCount, "lead")} | ${plural(
      contactsCount,
      "contact"
    )}`;
  });
  const debounceSearch = debounce(getData);

  onMounted(() => {
    getData();
  });

  watch(
    () => searchQuery.value,
    () => {
      selectedLeadOrContactId.value = "";
      debounceSearch();
    }
  );

  const close = () => {
    emits("close");
    searchQuery.value = "";
  };

const __returned__ = { toast, emits, isLoading, searchQuery, leads, contacts, activeLoaders, selectedLeadOrContactId, leadsAndContactsList, form, rules, v$, isEmailValid, props, getData, onSendToLeadOrContact, getLeads, getContacts, shareLeadsLinks, shareContactLink, sendReservationLinkToNewLead, itemsCountText, debounceSearch, close, Modal, InputSearch, computed, onMounted, reactive, ref, watch, get store() { return store }, get LeadInterface() { return LeadInterface }, get plural() { return plural }, get debounce() { return debounce }, get useVuelidate() { return useVuelidate }, get useToast() { return useToast }, get email() { return email }, get required() { return required }, get ContactInterface() { return ContactInterface }, get SendContractFormType() { return SendContractFormType } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})